import { createCodedError } from '~/common/errors/CodedError';
import { sylvanus } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { PostUserMessageParams } from '../transformers/conversationalAi';
import {
  Conversation,
  ConversationMessage,
  ExternalEntity,
  ListConversationsApiV1ConversationsGetApiArg,
  PageConversation,
  UserRating,
} from '../types/sylvanus/conversation';
import { testIsAxiosError } from '../utils';

interface PostUserRatingParams {
  conversationId: Uuid;
  messageId: Uuid;
  userRating: Nullable<UserRating>;
  userComment?: Nullable<string>;
}

export const getAllConversations = async (
  params: ListConversationsApiV1ConversationsGetApiArg,
): Promise<PageConversation> => {
  return sylvanus.get('/conversations', {
    params: { entity_id: params.entityId, entity_type: params.entityType },
  });
};

export const createNewConversation = async (
  entity: ExternalEntity,
): Promise<Conversation> => {
  return sylvanus.post('/conversation', { entities: [entity] });
};

export const getExistingConversation = async (
  conversationId: Uuid,
): Promise<Conversation> => {
  try {
    return await sylvanus.get(`/conversation/${conversationId}`);
  } catch (error: unknown) {
    if (testIsAxiosError(error) && error.response) {
      throw createCodedError({
        message: error.message,
        statusCode: error.response.status,
        title: '',
      });
    }
    throw error;
  }
};

export const postNewUserMessage = async ({
  conversationId,
  message,
  context,
  metadata,
}: PostUserMessageParams): Promise<ConversationMessage> => {
  try {
    return await sylvanus.post(`/conversation/${conversationId}/message`, {
      message,
      context,
      metadata: metadata
        ? {
            question_id: metadata?.questionId,
            question_group_id: metadata?.questionGroupId,
          }
        : undefined,
    });
  } catch (error: unknown) {
    if (testIsAxiosError(error) && error.response) {
      throw createCodedError({
        message: error.message,
        statusCode: error.response.status,
        title: '',
      });
    }
    throw error;
  }
};

export const postUserRating = async ({
  conversationId,
  messageId,
  userRating,
  userComment,
}: PostUserRatingParams): Promise<ConversationMessage> => {
  return sylvanus.patch(
    `/conversation/${conversationId}/message/${messageId}`,
    {
      user_rating: userRating,
      user_comment: userComment,
    },
  );
};

export const acceptLegalDisclaimer = async () => {
  return sylvanus.post('/user/accept-disclaimer');
};

export const interruptAiMessage = async (conversationId: Uuid) => {
  return sylvanus.post(`/conversation/${conversationId}/interrupt`);
};
